import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import fetchProduct from '@/api/productDetail';
import { NutritionFacts, productNutritionFacts } from '@/api/productNutritionFacts';
import { productTagReferences, TagReference } from '@/api/productTagReferences';
import { useCallback } from '@/composables/useCallback';
import { Product } from '@/graphql/productQuery';
import { findAttributeByName } from '@/lib/product/attributes';
import { NutsProduct } from '@/utils/product';

export const useProductStore = (productKey: string) =>
  defineStore(`pdp:${productKey}`, () => {
    const rawProduct = ref<Product>();
    const nutritionFacts = ref<NutritionFacts>();
    const tagsReferences = ref<TagReference[]>();

    const getProduct = useCallback(async () => {
      if (rawProduct.value) {
        getProduct.isPending = false;
        return;
      }

      const [
        {
          body: { data: response },
        },
        { HTMLCleaner },
      ] = await Promise.all([fetchProduct(productKey), import('@/utils/html_cleaner')]);

      rawProduct.value = {
        ...response.product,
        masterData: {
          current: {
            ...response.product.masterData.current,
            description: HTMLCleaner.clean(response.product.masterData.current.description),
            healthTips: HTMLCleaner.clean(
              findAttributeByName(
                response.product.masterData.current.variants[0]?.attributesRaw,
                'healthTips',
              )?.en,
            ),
            contentList: HTMLCleaner.clean(
              findAttributeByName(
                response.product.masterData.current.masterVariant.attributesRaw,
                'contentList',
              )?.en,
            ),
            testDescription: HTMLCleaner.clean(
              findAttributeByName(
                response.product.masterData.current.masterVariant.attributesRaw,
                'testDescription',
              )?.en,
            ),
          },
        },
      };
    });

    const product = computed(() => {
      if (!getProduct.error && !getProduct.isPending && !rawProduct.value) {
        throw new Error('Product information not loaded yet');
      }
      return rawProduct.value ? NutsProduct.fromCT(rawProduct.value) : undefined;
    });

    const getNutritionFacts = useCallback(async () => {
      if (nutritionFacts.value) {
        return;
      }

      const { value } = await productNutritionFacts(productKey);
      nutritionFacts.value = value;
    });

    const getTagReferences = useCallback(async () => {
      if (tagsReferences.value) {
        return;
      }

      tagsReferences.value = await productTagReferences(productKey);
    });

    return {
      getProduct,
      getNutritionFacts,
      getTagReferences,
      nutritionFacts,
      product,
      rawProduct,
      tagsReferences,
    };
  })();
